import { useMemo } from "react";
import { SearchBar } from "./SearchBar";
import { TreeRow } from "./TreeRow";
import { Pagination } from "./Pagination";
import { filterTree, sortTree, buildVisibleRows, countItems } from "./helpers";
import { MdSearch, MdKeyboardArrowRight } from "react-icons/md";

const PAGE_SIZE = 8;

// ─── ListPanel ────────────────────────────────────────────────────────────────
export function ListPanel({
   title,
   tree,
   selectedIds,
   expandedIds,
   onToggleExpand,
   onSelect,
   onDoubleClick,
   onDragStart,
   onDrop,
   onDragOver,
   searchQuery,
   onSearchChange,
   page,
   onPageChange,
   listId,
   isDragOver,
   sortMode,
   onSortChange,
}) {

   const filtered = useMemo(
      () => filterTree(tree, searchQuery),
      [tree, searchQuery]
   );

   // Apply sort after filter
   const sorted = useMemo(
      () => sortTree(filtered, sortMode),
      [filtered, sortMode]
   );

   // Pagination operates on top-level nodes of the filtered+sorted tree
   const totalTopLevel = sorted.length;
   const totalPages = Math.max(1, Math.ceil(totalTopLevel / PAGE_SIZE));
   const pagedTopLevel = sorted.slice(
      (page - 1) * PAGE_SIZE,
      page * PAGE_SIZE
   );

   // Auto-expand groups that contain search matches
   const searchExpandedIds = useMemo(() => {
      if (!searchQuery) return expandedIds;
      const extra = new Set(expandedIds);
      function collectGroupIds(nodes) {
         for (const node of nodes) {
            if (node.type === "group") {
               extra.add(node.id);
               collectGroupIds(node.children || []);
            }
         }
      }
      collectGroupIds(filtered);
      return extra;
   }, [searchQuery, expandedIds, filtered]);

   const visibleRows = useMemo(
      () => buildVisibleRows(pagedTopLevel, searchExpandedIds),
      [pagedTopLevel, searchExpandedIds]
   );

   const itemCount = countItems(tree);

   return (
      <div
         className={`flex flex-col text-(--textColor)! flex-1 min-w-0 rounded-xl border! transition-colors duration-150 ${isDragOver
            ? "border-(--primaryColor)! bg-(--listHeaderBackgroundColor)"
            : "border-(--borderColor)! bg-(--containerBackgroundColor) shadow-(--box-shadow)"
            }`}
         onDragOver={(e) => {
            e.preventDefault();
            onDragOver(listId);
         }}
         onDrop={(e) => onDrop(e, listId)}
         onDragLeave={() => onDragOver(null)}
      >
         {/* Header */}
         <div className="px-4! py-3! border-b! border-(--borderColor)!">
            <div className="flex items-center justify-between mb-2!">
               <span className="text-base text-(--textColor) font-extrabold!">{title}</span>
               <span className="text-[10px]! bg-(--listHeaderBackgroundColor) rounded-full px-2! py-1!">
                  <span className="text-(--textColor) opacity-60">
                     {itemCount} {itemCount !== 1 ? "itens" : "item"}
                  </span>
               </span>
            </div>

            {/* Search + Sort row */}
            <div className="flex items-center gap-2!">
               <div className="relative shrink-0 w-20!">
                  <select
                     value={sortMode || ""}
                     onChange={(e) => { onSortChange(e.target.value || null); }}
                     title="Ordenar lista"
                     className="appearance-none w-full! text-xs border! border-(--borderColor)! rounded-md pl-1.5! h-[32px]! pr-5! py-[0.3135rem]! box-border! bg-(--inputBackgroundColor) text-(--textColor) cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent! transition"
                  >
                     <option value="">—</option>
                     <option value="asc">A-Z</option>
                     <option value="desc">Z-A</option>
                     <option value="folders-asc">📁 A-Z</option>
                  </select>
                  <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-(--textColor) rotate-90">
                     <MdKeyboardArrowRight size={16} />
                  </span>
               </div>
               <div className="flex-1 min-w-0">
                  <SearchBar
                     value={searchQuery}
                     onChange={(v) => {
                        onSearchChange(v);
                        onPageChange(1);
                     }}
                     placeholder="Buscar…"
                  />
               </div>
            </div>
         </div>

         {/* Rows */}
         <div className="flex-1 overflow-y-auto min-h-0 py-1.5! px-1.5!">
            {visibleRows.length === 0 ? (
               <div className="flex flex-col items-center justify-center h-full py-8! text-gray-300 select-none">
                  <MdSearch size={32} />
                  <p className="text-sm mt-2!">Nenhum item</p>
               </div>
            ) : (
               visibleRows.map(({ node, depth }) => (
                  <TreeRow
                     key={node.id}
                     node={node}
                     depth={depth}
                     isSelected={selectedIds.has(node.id)}
                     isExpanded={searchExpandedIds.has(node.id)}
                     onToggleExpand={onToggleExpand}
                     onSelect={onSelect}
                     onDoubleClick={onDoubleClick}
                     onDragStart={onDragStart}
                     listId={listId}
                     searchQuery={searchQuery}
                  />
               ))
            )}
         </div>

         {/* Pagination */}
         {totalPages > 1 && (
            <div className="px-4! pb-3! pt-1! border-t! border-(--borderColor)!">
               <Pagination
                  page={page}
                  totalPages={totalPages}
                  onChange={onPageChange}
               />
            </div>
         )}
      </div>
   );
}